import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

const authHeaders = (json = true) => {
  const headers = { Authorization: `Bearer ${getToken()}` };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
};

async function readError(response) {
  try {
    const data = await response.json();
    return data.message || data.title || `خطأ ${response.status}`;
  } catch {
    try {
      const text = await response.text();
      return text || `خطأ ${response.status}`;
    } catch {
      return `خطأ ${response.status}`;
    }
  }
}

export const TICKET_STATUSES = [
  "Open",
  "In Progress",
  "Waiting for Customer",
  "Resolved",
  "Closed",
  "Invalid / No Issue Found",
];

export const TICKET_ACTIVE_STATUSES = ["Open", "In Progress", "Waiting for Customer"];

export const TICKET_PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export const fetchTickets = async (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  });
  const response = await fetch(`${API_BASE_URL}/Ticket/List?${searchParams}`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchTicketStatusCounts = async () => {
  const response = await fetch(`${API_BASE_URL}/Ticket/StatusCounts`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchTicketById = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/Get/${id}`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchActiveTicketsForOrder = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/ActiveForOrder/${orderId}`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

const normalizeTicketRows = (data) => {
  const items = data?.items || data?.Items || (Array.isArray(data) ? data : []);
  return Array.isArray(items) ? items : [];
};

export const fetchTicketsForOrders = async (orderIds = []) => {
  const ids = [...new Set((orderIds || []).map(Number).filter((id) => Number.isInteger(id) && id > 0))];
  if (!ids.length) return [];

  const loadOne = async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/Ticket/ForOrder/${orderId}`, {
        headers: authHeaders(false),
      });
      if (response.ok) {
        const data = await response.json();
        const row = Array.isArray(data) ? data[0] : data;
        const tickets = row?.tickets || row?.Tickets || [];
        if (tickets.length) return { orderId, tickets };
      }
    } catch (err) {
      console.error("ForOrder lookup failed", orderId, err);
    }
    try {
      const data = await fetchTickets({
        orderId,
        pageSize: 20,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      return { orderId, tickets: normalizeTicketRows(data) };
    } catch (err) {
      console.error("Ticket list lookup failed", orderId, err);
      return { orderId, tickets: [] };
    }
  };

  return Promise.all(ids.map(loadOne));
};

export const fetchTicketOrderContext = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/OrderContext/${orderId}`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const createTicket = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/Create`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const deleteTicket = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}`, {
    method: "DELETE",
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const retryTicketCreatedWhatsApp = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}/RetryCreatedWhatsApp`, {
    method: "POST",
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const updateTicketStatus = async (id, status, note) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}/Status`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ status, note }),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const updateTicketPriority = async (id, priority, note) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}/Priority`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ priority, note }),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const assignTicket = async (id, assignedToUserId, note) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}/Assign`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ assignedToUserId, note }),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const addTicketNote = async (id, note) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/${id}/Notes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ note }),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchAssignableUsers = async () => {
  const response = await fetch(`${API_BASE_URL}/Ticket/AssignableUsers`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchTicketProblemTypes = async (activeOnly = false) => {
  const response = await fetch(
    `${API_BASE_URL}/Ticket/ProblemTypes?activeOnly=${activeOnly ? "true" : "false"}`,
    { headers: authHeaders(false) }
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const createTicketProblemType = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/ProblemTypes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const updateTicketProblemType = async (id, payload) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/ProblemTypes/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const toggleTicketProblemType = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Ticket/ProblemTypes/${id}/Toggle`, {
    method: "PATCH",
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

export const fetchTicketMeta = async () => {
  const response = await fetch(`${API_BASE_URL}/Ticket/Meta`, {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
};

/** Read a File as base64 (without data-URL prefix) + metadata for ticket uploads. */
export const fileToTicketAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file selected"));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve({
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        base64Content: base64,
        previewUrl: result.startsWith("data:") ? result : null,
        isVideo: (file.type || "").startsWith("video/"),
      });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
